import { apiClient, getApiUrl } from '../lib/apiClient';
import type { 
  SystemMetric,
  BackgroundJob,
  Notification,
  TrainingDataset,
  ModelVersion,
  HealthCheckResponse
} from '../types/api';
import { 
  extractListData, 
  extractSingleData,
  extractPagination,
  type RailsWrappedListResponse,
  type RailsSingleWrappedResponse,
  type RailsDirectListResponse
} from '../utils/railsResponseHandler';

// System monitoring
export const getSystemMetrics = async (params?: {
  metric_type?: string;
  start_date?: string;
  end_date?: string;
}): Promise<{ metrics: SystemMetric[]; pagination?: any }> => {
  const response = await apiClient.get<RailsWrappedListResponse<SystemMetric>>(getApiUrl('/system_metrics'), { params });
  const metrics = extractListData<SystemMetric>(response.data);
  const pagination = extractPagination(response.data);
  return { metrics, pagination };
};

export const getBackgroundJobs = async (params?: {
  status?: string;
  job_type?: string;
  page?: number;
  per_page?: number;
}): Promise<{ jobs: BackgroundJob[]; pagination?: any }> => {
  const response = await apiClient.get<RailsWrappedListResponse<BackgroundJob>>(getApiUrl('/background_jobs'), { params });
  const jobs = extractListData<BackgroundJob>(response.data);
  const pagination = extractPagination(response.data);
  return { jobs, pagination };
};

export const getSystemNotifications = async (params?: {
  page?: number;
  per_page?: number;
  notification_type?: string;
}): Promise<{ notifications: Notification[]; pagination?: any }> => {
  const response = await apiClient.get<RailsDirectListResponse<Notification>>(getApiUrl('/notifications'), { params });
  const notifications = extractListData<Notification>(response.data);
  const pagination = extractPagination(response.data);
  return { notifications, pagination };
};

// Health check - No authentication required
export const getHealthStatus = async (): Promise<HealthCheckResponse> => {
  // Health endpoint doesn't require authentication and returns direct response
  const response = await apiClient.get<HealthCheckResponse>(getApiUrl('/health'));
  return response.data;
};

// Training and AI model management
export const getTrainingDatasets = async (params?: {
  dataset_type?: string;
  status?: string;
  page?: number;
  per_page?: number;
}): Promise<{ datasets: TrainingDataset[]; pagination?: any }> => {
  const response = await apiClient.get<RailsWrappedListResponse<TrainingDataset>>(getApiUrl('/training_datasets'), { params });
  const datasets = extractListData<TrainingDataset>(response.data);
  const pagination = extractPagination(response.data);
  return { datasets, pagination };
};

export const getTrainingJobs = async (params?: {
  status?: string;
  page?: number;
  per_page?: number;
}): Promise<{ jobs: any[]; pagination?: any }> => {
  const response = await apiClient.get<RailsWrappedListResponse<any>>(getApiUrl('/training_jobs'), { params });
  const jobs = extractListData<any>(response.data);
  const pagination = extractPagination(response.data);
  return { jobs, pagination };
};

export const startTrainingJob = async (id: string): Promise<any> => {
  const response = await apiClient.post<RailsSingleWrappedResponse<any>>(getApiUrl(`/training_jobs/${id}/start`));
  return extractSingleData<any>(response.data);
};

export const stopTrainingJob = async (id: string): Promise<any> => {
  const response = await apiClient.post<RailsSingleWrappedResponse<any>>(getApiUrl(`/training_jobs/${id}/stop`));
  return extractSingleData<any>(response.data);
};

export const getTrainingProgress = async (id: string): Promise<any> => {
  const response = await apiClient.get<RailsSingleWrappedResponse<any>>(getApiUrl(`/training_jobs/${id}/progress`));
  return extractSingleData<any>(response.data);
};

export const getModelVersions = async (params?: {
  model_type?: string;
  is_deployed?: boolean;
  page?: number;
  per_page?: number;
}): Promise<{ versions: ModelVersion[]; pagination?: any }> => {
  const response = await apiClient.get<RailsWrappedListResponse<ModelVersion>>(getApiUrl('/model_versions'), { params });
  const versions = extractListData<ModelVersion>(response.data);
  const pagination = extractPagination(response.data);
  return { versions, pagination };
};

export const deployModelVersion = async (id: string): Promise<ModelVersion> => {
  const response = await apiClient.post<RailsSingleWrappedResponse<ModelVersion>>(getApiUrl(`/model_versions/${id}/deploy`));
  return extractSingleData<ModelVersion>(response.data);
};

export const rollbackModelVersion = async (id: string): Promise<ModelVersion> => {
  const response = await apiClient.post<RailsSingleWrappedResponse<ModelVersion>>(getApiUrl(`/model_versions/${id}/rollback`));
  return extractSingleData<ModelVersion>(response.data);
};